import React, { useState } from "react";
import { useEffect } from "react";
import { Camera, X, Home } from "lucide-react";
import { QRScanner } from "../components/QRScanner";
import { CameraCapture } from "../components/CameraCapture";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { useAuth } from "../contexts/AuthContext";
import { Header } from "../components/Header";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import type { Location } from "../lib/supabase";
import { getUserCurrentEventId, getUserRole, submitPenalty, type PenaltySubmissionData } from "../lib/api";

export function Dnf() {
  return (
    <ProtectedRoute>
      <DnfContent />
    </ProtectedRoute>
  );
}

function DnfContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [raceNumber, setRaceNumber] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(() => {
    // Load saved location from session storage on initial load
    return sessionStorage.getItem('currentLocation') || "";
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [athleteName, setAthleteName] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [dnfReason, setDnfReason] = useState("");
  const [currentEventId, setCurrentEventId] = useState<number | null>(null);
  const [raceoffroleid, setRaceoffroleid] = useState<number | null>(null);
  const [judgestation, setJudgestation] = useState<number | null>(null);
  
  // Dynamic data from database
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Handle location change with persistence
  const handleLocationChange = (value: string) => {
    setSelectedLocation(value);
    sessionStorage.setItem('currentLocation', value);
  };

  // Load data from database on component mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      
      // Load locations
      const { data: locationsData, error: locationsError } = await supabase
        .from('location')
        .select('locationid, locationname, penaltytime')
        .order('locationid');

      if (locationsData && !locationsError) {
        setLocations(locationsData);
        
        // Handle initial location selection after locations are loaded
        const savedLocation = sessionStorage.getItem('currentLocation');
        if (savedLocation && locationsData.some(loc => loc.locationid.toString() === savedLocation)) {
          // Use saved location if it exists in the loaded locations
          setSelectedLocation(savedLocation);
        } else {
          // Clear invalid saved location and use first available
          sessionStorage.removeItem('currentLocation');
          if (locationsData.length > 0) {
            const firstLocationId = locationsData[0].locationid.toString();
            setSelectedLocation(firstLocationId);
            sessionStorage.setItem('currentLocation', firstLocationId);
          }
        }
      }
      // Load user's current event ID
      if (user?.email) {
        const eventResponse = await getUserCurrentEventId(user.email);
        if (eventResponse.success && eventResponse.data) {
          setCurrentEventId(eventResponse.data.currenteventid);
        } else {
          console.log('Failed to get user current event ID:', eventResponse.error);
          setCurrentEventId(null); // Will fallback to 3 in database insert
        }
      }

      // Load user's race official ID using getUserRole function
      if (user?.email) {
        const roleResponse = await getUserRole(user.email);
        if (roleResponse.success && roleResponse.data) {
          setRaceoffroleid(roleResponse.data.roleid);
          setJudgestation(roleResponse.data.judgestation);
        } else {
          setRaceoffroleid(null); // Will fallback to 1 in database insert
          setJudgestation(null);
        }
      }
      
      setIsLoading(false);
    };

    loadData();
  }, [user?.email]);

  // Handle judgestation-based location selection for roleid = 4
  useEffect(() => {
    if (raceoffroleid === 4 && judgestation !== null && locations.length > 0) {
      const judgeStationLocation = locations.find(loc => loc.locationid === judgestation);
      if (judgeStationLocation) {
        const judgeStationLocationId = judgeStationLocation.locationid.toString();
        setSelectedLocation(judgeStationLocationId);
        sessionStorage.setItem('currentLocation', judgeStationLocationId);
      }
    }
  }, [raceoffroleid, judgestation, locations]);

  const handleQRScan = (result: string) => {
    console.log('QR Code scanned:', result);
    
    // Try to extract raceNumber and athleteName from URL query parameters
    try {
      const url = new URL(result);
      const raceNumberParam = url.searchParams.get('raceNumber');
      const athleteNameParam = url.searchParams.get('athleteName');
      
      if (raceNumberParam) {
        setRaceNumber(raceNumberParam);
      } else {
        // Fallback: if the scanned data looks like a valid alphanumeric race number (5 chars + 2 digits)
        if (/^[A-Za-z]{5}\d{2}$/.test(result)) {
          setRaceNumber(result);
        } else {
          alert(`QR Code scanned but no valid race number found: ${result}`);
        }
      }
      
      // Set athlete name for display in green box
      if (athleteNameParam) {
        setAthleteName(athleteNameParam);
      } else {
        setAthleteName(null);
      }
    } catch (error) {
      // If it's not a valid URL, try the fallback approach
      if (/^[A-Za-z]{5}\d{2}$/.test(result)) {
        setRaceNumber(result);
      } else {
        alert(`QR Code scanned: ${result}`);
      }
      setAthleteName(null);
    }
  };

  const handlePhotoCapture = (photoDataUrl: string) => {
    setCapturedPhoto(photoDataUrl);
    setShowCamera(false);
  };

  const handleRemovePhoto = () => {
    setCapturedPhoto(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!selectedLocation || !raceNumber || !user?.email) {
      setSubmitMessage("Please fill in all required fields. Make sure you are logged in with a valid email.");
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage("");

    try {
      // Submit DNF record using submitPenalty function
      const submissionData: PenaltySubmissionData = {
        locationId: parseInt(selectedLocation),
        raceNumber: raceNumber,
        penaltyId: 3, // For DNF
        penaltyTime: 0, // DNF has no penalty time
        judgeEmail: user?.email || '',
        numPenalties: 1,
        photoUrl: capturedPhoto || undefined,
        eventid: currentEventId || 7, // Use user's current event ID or fallback to 7
        raceoffroleid: raceoffroleid || 4, // Use user's race official ID or fallback to 5
        notes: dnfReason ? `DNF - ${dnfReason}` : 'DNF - Did Not Finish'
      };

      const result = await submitPenalty(submissionData);
      
      if (result.success) {
        setSubmitMessage("DNF recorded for #" + raceNumber);
        // Clear form fields but keep the success message
        setRaceNumber("");
        setDnfReason("");
        setCapturedPhoto(null);
        setAthleteName(null);

      // Clear success message after 2 seconds
      setTimeout(() => {
        setSubmitMessage("");
      }, 2000);        
      } else {
        setSubmitMessage(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error submitting DNF:', error);
      setSubmitMessage(`Error: Failed to record DNF. Please try again.`);
    }
    
    setIsSubmitting(false);
  };

  const handleClear = () => {
    setRaceNumber("");
    setSubmitMessage("");
    setCapturedPhoto(null);
    setAthleteName(null);
    setDnfReason("");
    // Don't clear location as it should persist during the session
  };

  return (
    <div className="pen-screen font-brutal flex flex-col min-h-screen">
      <Header 
        locations={locations}
        selectedLocation={selectedLocation}
        onLocationChange={handleLocationChange}
        isLoading={isLoading}
      />
      <div className="px-4 py-4 flex flex-col gap-3 max-w-lg mx-auto w-full flex-1">
        <QRScanner
          onScan={handleQRScan}
          stationName={
            locations.find((loc) => loc.locationid.toString() === selectedLocation)?.locationname ||
            undefined
          }
        />

        {isLoading && (
          <div className="text-center text-sw-muted text-xs uppercase tracking-wide font-extrabold font-brutal">
            Loading...
          </div>
        )}

        {athleteName && (
          <div className="bg-[#0e2a16] border-2 border-[rgba(48,176,53,0.35)] text-[#c7f0cb] px-3 py-2.5 text-[13px] text-center font-brutal">
            Athlete: <span className="font-extrabold">{athleteName}</span>
          </div>
        )}

        <form className="flex flex-col gap-3 flex-1" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="raceNumber" className="pen-label">
              Race Number <span className="text-sw-red">*</span>
            </label>
            <input
              id="raceNumber"
              placeholder="Enter Race Number"
              value={raceNumber}
              onChange={(e) => setRaceNumber(e.target.value)}
              className="pen-input !rounded-none font-brutal font-light"
              required
            />
          </div>

          <div>
            <label htmlFor="dnfReason" className="pen-label">
              DNF Reason
            </label>
            <input
              id="dnfReason"
              placeholder="e.g., Injury, Failure, Withdrawal"
              value={dnfReason}
              onChange={(e) => setDnfReason(e.target.value)}
              className="pen-input !rounded-none font-brutal"
            />
          </div>

          <div className="flex items-center justify-between gap-3 border-2 border-dashed border-sw-line px-3.5 py-3 bg-black">
            <span className="text-[11px] text-sw-muted font-extrabold uppercase tracking-[0.06em] font-brutal leading-snug">
              Optional · Photo of Race Number
            </span>
            <div className="flex items-center gap-2">
              {capturedPhoto && (
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="w-[42px] h-[42px] border-2 border-sw-red bg-sw-nav grid place-items-center text-sw-red hover:bg-sw-red hover:text-black"
                  title="Remove Photo"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowCamera(true)}
                className={`w-[42px] h-[42px] border-2 grid place-items-center bg-sw-nav ${
                  capturedPhoto
                    ? 'border-sw-green text-sw-green'
                    : 'border-sw-border text-sw-muted hover:border-sw-yellow hover:text-sw-yellow'
                }`}
                title={capturedPhoto ? "Photo Captured - Click to Retake" : "Take Photo"}
              >
                <Camera className="w-5 h-5" />
              </button>
            </div>
          </div>

          {capturedPhoto && (
            <img
              src={capturedPhoto}
              alt="Captured race number"
              className="w-full h-32 object-cover border-2 border-sw-border"
            />
          )}

          {submitMessage && (
            <div
              className={`${
                submitMessage.includes('Error') ? 'pen-error' : 'pen-success'
              } !rounded-none font-brutal`}
            >
              {submitMessage}
            </div>
          )}

          <div className="flex gap-2.5 my-5">
            <button
              type="submit"
              className="pen-btn flex-1 !rounded-none !text-[15px] font-brutal font-black bg-sw-red text-black border-sw-red hover:bg-black hover:text-sw-red"
              disabled={isSubmitting || isLoading || !selectedLocation}
            >
              {isSubmitting ? 'Recording DNF...' : 'Record DNF'}
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="pen-btn-ghost flex-1 !rounded-none !text-[15px] font-brutal font-black !border-2 !border-sw-border !bg-transparent hover:!bg-white hover:!text-black"
              disabled={isSubmitting}
            >
              Clear
            </button>
          </div>

          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="pen-btn-ghost w-full mt-auto !rounded-none !text-[15px] font-brutal font-black !border-2 !border-sw-border"
          >
            GO BACK <Home className="w-4 h-4" />
          </button>
        </form>

        {showCamera && (
          <CameraCapture
            onCapture={handlePhotoCapture}
            onClose={() => setShowCamera(false)}
          />
        )}
      </div>
    </div>
  );
}
